import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Tooltip, Dropdown } from 'antd';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(document.body.classList.contains('sidebar-collapse'));

  // Detect sidebar collapse changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsCollapsed(document.body.classList.contains('sidebar-collapse'));
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Keep Reports parent active for all report-related routes used in this app
  const reportPaths = ['/reports', '/ai-chat-summary-list'];

  // Initialize state directly from URL to avoid reset on reload
  const [openMenu, setOpenMenu] = useState<string | null>(() => {
    const currentPath = window.location.pathname.replace(/\/$/, "");
    if (reportPaths.some(p => currentPath === p.replace(/\/$/, ""))) {
      return 'vitals';
    }
    return null;
  });

  useEffect(() => {
    const currentPath = location.pathname.replace(/\/$/, "");

    const activeParent = sidebarItems.find(item =>
      item.children?.some(child =>
        currentPath.startsWith(child.path.replace(/\/$/, ""))
      )
    );

    if (activeParent?.id) {
      setOpenMenu(activeParent.id);
    }
  }, [location.pathname]);

  const handleParentClick = (menu: string, firstChildPath: string) => {
    if (openMenu === menu) {
      setOpenMenu(null);
    } else {
      setOpenMenu(menu);
      navigate(firstChildPath);
    }
  };

  const isActive = (path: string) => {
    const currentPath = location.pathname.replace(/\/$/, "");
    const targetPath = path.replace(/\/$/, "");
    return currentPath === targetPath;
  };

  const isVitalsActive = reportPaths.some(p => isActive(p));

  interface SidebarItem {
    path?: string;
    icon: string;
    label: string;
    id?: string;
    isParent?: boolean;
    children?: { path: string; label: string }[];
    firstPath?: string;
  }

  const sidebarItems: SidebarItem[] = [
    { path: '/dashboard', icon: 'fa-th-large', label: 'Dashboard' },
    { path: '/patient-family-member', icon: 'fa-users', label: 'Family Search' },
    // { path: '/appointmentList', icon: 'fa-calendar', label: 'Appointments' },
    { path: '/calendar', icon: 'fa-calendar-check-o', label: 'Calendar' },
    {
      id: 'vitals',
      icon: 'fa-file-text',
      label: 'Reports',
      isParent: true,
      children: [

        { path: '/reports', label: 'Medical Reports' },
        { path: '/ai-chat-summary-list', label: 'MyTara Reports' },

      ],
      firstPath: '/reports'
    },
    { path: '/my-vital-reports', icon: 'fa-heartbeat', label: 'Vitals Reports' },
    { path: '/medication', icon: 'fa-solid fa-pills', label: 'Medication' },
    // { path: '/coin-wallet', icon: 'fa-trophy', label: 'Rewards & Coins' },
  ];

  return (
    <aside className="main-sidebar">
      <section className="sidebar position-relative">
        <div className="multinav">
          <div className="multinav-scroll" style={{ height: '100%' }}>
            <ul className="sidebar-menu" style={{ listStyleType: 'none' }}>
              {sidebarItems.map((item) => {
                const isActiveItem = item.isParent ? isVitalsActive : isActive(item.path!);

                if (item.isParent) {
                  const menuItems = item.children?.map(child => ({
                    key: child.path,
                    label: <Link to={child.path} style={{ color: '#ffffff', padding: '5px 10px', display: 'block' }}>{child.label}</Link>,
                  }));

                  const parentLink = (
                    <a href="#!" onClick={(e) => { e.preventDefault(); handleParentClick(item.id!, item.firstPath!); }}>
                      <i className={`fa ${item.icon}`}></i>
                      <span>{item.label}</span>
                      <span className="pull-right-container">

                        <i
                          className="fa fa-angle-right"
                          style={{
                            transform: String(openMenu) === String(item.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            display: 'inline-block', 
                          }}
                        ></i>

                      </span>
                    </a>
                  );

                  return (
                    <li key={item.id} className={`treeview ${isActiveItem ? 'active' : ''} ${openMenu === item.id ? 'menu-open' : ''}`}>
                      {isCollapsed ? (
                        <Dropdown
                          menu={{ items: menuItems, className: 'sidebar-flyout-menu', style: { background: '#1e1e2d', borderRadius: '8px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' } }}
                          placement="bottomRight"
                          trigger={['click']}
                        >
                          <Tooltip title={item.label} placement="right">
                            {parentLink}
                          </Tooltip>
                        </Dropdown>
                      ) : (
                        parentLink
                      )}
                      <ul className="treeview-menu" style={{ listStyleType: 'none', display: (openMenu === item.id) ? 'block' : 'none' }}>
                        {item.children?.map(child => (
                          <li key={child.path} className={isActive(child.path) ? 'active' : ''}>
                            <Link to={child.path}><i className="fa fa-circle"></i>{child.label}</Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                }

                const singleLink = (
                  <Link to={item.path!} onClick={() => setOpenMenu(null)}>
                    <i className={`fa ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                );

                return (
                  <li key={item.path} className={`treeview ${isActiveItem ? 'active' : ''}`} style={{ listStyleType: 'none' }}>
                    {isCollapsed ? (
                      <Tooltip title={item.label} placement="right">
                        {singleLink}
                      </Tooltip>
                    ) : (
                      singleLink
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="sidebar-widgets">
          <div className="copyright text-center m-25">
            <p>
              <strong className="d-block">MyTara</strong>
              © {new Date().getFullYear()} All Rights Reserved
            </p>
          </div>
        </div>
      </section>
    </aside>
  );
};

export default Sidebar;
